import { useMemo } from "react";
import { useState } from "react";
import SunCalc from "suncalc";

function formatTime(date) {
  return new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function getDayStart(date) {
  const dayStart = new Date(date);
  dayStart.setHours(0, 0, 0, 0);
  return dayStart;
}

function findExtremaCenters(dayStart, lat, lng) {
  const stepMinutes = 5;
  const samples = [];

  for (let minute = 0; minute <= 24 * 60; minute += stepMinutes) {
    const t = new Date(dayStart.getTime() + minute * 60 * 1000);
    const position = SunCalc.getMoonPosition(t, lat, lng);
    samples.push({ t, altitude: position.altitude });
  }

  if (samples.length === 0) return [];

  let maxSample = samples[0];
  let minSample = samples[0];

  for (const sample of samples) {
    if (sample.altitude > maxSample.altitude) maxSample = sample;
    if (sample.altitude < minSample.altitude) minSample = sample;
  }

  return [
    { type: "overhead", center: maxSample.t },
    { type: "underfoot", center: minSample.t },
  ];
}

export default function useSolunarMajor(location) {
  const [refreshToggle, setRefreshToggle] = useState(0);

  const refresh = () => setRefreshToggle((c) => c + 1);

  const data = useMemo(() => {
    if (!location?.lat || !location?.lng) {
      return { label: "GPS needed", status: "warn" };
    }

    try {
      const now = new Date(Date.now() + refreshToggle * 0);
      const todayStart = getDayStart(now);
      const tomorrowStart = new Date(todayStart.getTime() + 24 * 60 * 60 * 1000);

      const todayCenters = findExtremaCenters(todayStart, location.lat, location.lng);
      const tomorrowCenters = findExtremaCenters(tomorrowStart, location.lat, location.lng);
      const allCenters = [...todayCenters, ...tomorrowCenters].sort((a, b) => a.center.getTime() - b.center.getTime());

      if (allCenters.length === 0) {
        return { label: "Unavailable", status: "warn" };
      }

      const oneHour = 60 * 60 * 1000;

      const activeWindow = allCenters.find(({ center }) => {
        const start = center.getTime() - oneHour;
        const end = center.getTime() + oneHour;
        const nowTime = now.getTime();
        return nowTime >= start && nowTime <= end;
      });

      if (activeWindow) {
        const windowEnd = new Date(activeWindow.center.getTime() + oneHour);
        return {
          label: `Now · until ${formatTime(windowEnd)}`,
          status: "good",
          type: activeWindow.type,
        };
      }

      const nextCenter = allCenters.find(({ center }) => center.getTime() > now.getTime()) || allCenters[0];

      return {
        label: formatTime(nextCenter.center),
        status: "good",
        type: nextCenter.type,
      };
    } catch (err) {
      console.error("Solunar major calculation failed:", err);
      return { label: "Unavailable", status: "warn" };
    }
  }, [location?.lat, location?.lng, refreshToggle]);

  return { ...data, refresh };
}
